import FeatureGates from '@atlaskit/feature-gate-js-client';
import type { PropsWithChildren } from 'react';
import { RenderTrackerProvider } from './render-tracker';
import { StableAnaltyicsProvider } from './stable-analytics';
import { StableChoreographerProvider } from './stable-choreographer';
import { StableContextProvider } from './stable-context';
import { StablePlacementProvider } from './stable-placement';
import { StableSubscriptionConfigProvider } from './stable-graphql-subscription';
import { StableSubscriptionAdapterProvider } from '@atlassian/post-office-use-subscription/adapter-provider';
import type { SubscriptionConfig } from './stable-graphql-subscription/types';
import type {
	SubscriptionAdapter,
	SerializableRecord,
} from '@atlassian/post-office-use-subscription/types';
import { StableRouteProvider } from './stable-route';
import type { StableProvidersProps } from './types';
import type { StableProvider } from '../types';

export { PerformanceTracking, PerformanceTrackingSegment } from './performance-tracking';

/**
 * Feature gate name for GraphQL subscriptions in Post Office placements.
 * When disabled, subscriptions will not be enabled even if configured.
 */
// https://switcheroo.atlassian.com/ui/gates/8299833f-7cf4-453b-bd5b-16f3bcf81ddb/key/post-office_graphql_subscriptions_enabled
const POST_OFFICE_GRAPHQL_SUBSCRIPTIONS_FEATURE_GATE = 'post-office_graphql_subscriptions_enabled';

/**
 * Optional provider that wraps subscription providers only if feature gate is enabled.
 */
const OptionalPostOfficeSubscriptionsProvider = ({
	children,
	subscription,
	subscriptionAdapter,
}: PropsWithChildren<{
	subscription?: SubscriptionConfig;
	subscriptionAdapter?: SubscriptionAdapter<SerializableRecord>;
}>) => {
	const isPostOfficeSubscriptionsFeatureGateEnabled = FeatureGates.checkGate(
		POST_OFFICE_GRAPHQL_SUBSCRIPTIONS_FEATURE_GATE,
	);

	if (isPostOfficeSubscriptionsFeatureGateEnabled) {
		return (
			<StableSubscriptionConfigProvider subscription={subscription}>
				<StableSubscriptionAdapterProvider subscriptionAdapter={subscriptionAdapter}>
					{children}
				</StableSubscriptionAdapterProvider>
			</StableSubscriptionConfigProvider>
		);
	}

	return <>{children}</>;
};

export const StableProviders: StableProvider<StableProvidersProps> = ({
	children,
	context,
	placement,
	route,
	choreographer,
	subscription,
	subscriptionAdapter,
}) => {
	return (
		<StableContextProvider context={context}>
			<StablePlacementProvider placement={placement}>
				<StableRouteProvider route={route}>
					<StableAnaltyicsProvider>
						<OptionalPostOfficeSubscriptionsProvider
							subscription={subscription}
							subscriptionAdapter={subscriptionAdapter}
						>
							<StableChoreographerProvider
								choreographer={choreographer}
								placementId={placement.placementId}
							>
								<RenderTrackerProvider placementId={placement.placementId}>
									{children}
								</RenderTrackerProvider>
							</StableChoreographerProvider>
						</OptionalPostOfficeSubscriptionsProvider>
					</StableAnaltyicsProvider>
				</StableRouteProvider>
			</StablePlacementProvider>
		</StableContextProvider>
	);
};
